import {
  LayoutDashboard,
  Users,
  FileText,
  Calendar,
  BarChart3,
  Settings,
  Smartphone,
  CheckSquare,
  Camera,
  Wifi,
  RefreshCw,
  MapPin,
} from "lucide-react";

const adminFeatures = [
  {
    icon: LayoutDashboard,
    title: "Dashboard Unificado",
    description: "Visión completa de todas las operaciones en tiempo real. Métricas, estadísticas y análisis al instante.",
  },
  {
    icon: Users,
    title: "Gestión de Clientes",
    description: "Base de datos completa de clientes con historial de servicios, contratos y documentación.",
  },
  {
    icon: Calendar,
    title: "Planificación Inteligente",
    description: "Calendario visual de órdenes de trabajo con asignación automática de técnicos.",
  },
  {
    icon: FileText,
    title: "Documentos Automáticos",
    description: "Generación instantánea de partes de trabajo, presupuestos, facturas y mandatos SEPA en PDF.",
  },
  {
    icon: BarChart3,
    title: "Informes y Analíticas",
    description: "Reportes detallados de productividad, rendimiento y facturación con exportación a Excel.",
  },
  {
    icon: Settings,
    title: "Personalización Total",
    description: "Configura anomalías, checklists, tipos de trabajo y todos los parámetros del sistema.",
  },
];

const technicianFeatures = [
  {
    icon: Smartphone,
    title: "App Móvil PWA",
    description: "Instala como app nativa en cualquier dispositivo. Funciona en Android, iOS y desktop.",
  },
  {
    icon: CheckSquare,
    title: "Checklists Digitales",
    description: "Completa inspecciones de seguridad y mantenimiento con validación paso a paso.",
  },
  {
    icon: Camera,
    title: "Captura Multimedia",
    description: "Toma fotos y videos desde el móvil. Se adjuntan automáticamente al parte de trabajo.",
  },
  {
    icon: Wifi,
    title: "Modo Offline Completo",
    description: "Trabaja sin conexión en cualquier lugar. Los datos se guardan localmente con seguridad.",
  },
  {
    icon: RefreshCw,
    title: "Sincronización Automática",
    description: "Cuando recuperas conexión, todos los datos se sincronizan automáticamente a la nube.",
  },
  {
    icon: MapPin,
    title: "Geolocalización",
    description: "Registro automático de ubicación en cada trabajo para trazabilidad total.",
  },
];

export function LandingFeatures() {
  return (
    <section id="features" className="py-20 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-1/4 right-0 w-96 h-96 rounded-full bg-raven-violet/10 blur-3xl" />
      
      <div className="container mx-auto max-w-7xl relative z-10">
        <div className="text-center mb-16 animate-fade-in">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
            Funcionalidades <span className="landing-gradient-text">Completas</span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-4xl mx-auto">
            Diseñadas específicamente para el sector de <span className="font-semibold text-foreground">Seguridad</span> y <span className="font-semibold text-foreground">Contraincendios</span>
          </p>
        </div>

        {/* Admin Features */}
        <div className="mb-20">
          <h3 className="text-2xl font-bold mb-8 text-center">
            <span className="landing-gradient-text">Para Administradores</span>
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {adminFeatures.map((feature, index) => (
              <div
                key={index}
                className="group landing-glass-card p-6"
                style={{
                  animation: 'fade-in 0.6s ease-out backwards',
                  animationDelay: `${index * 0.1}s`
                }}
              >
                {/* Gradient overlay on hover */}
                <div className="absolute inset-0 bg-gradient-to-br from-raven-electric/0 to-raven-violet/0 group-hover:from-raven-electric/10 group-hover:to-raven-violet/10 transition-all duration-500 rounded-2xl" />
                
                {/* Icon */}
                <div className="relative mb-4 w-14 h-14 rounded-xl bg-gradient-to-br from-raven-electric to-raven-violet flex items-center justify-center group-hover:scale-110 group-hover:rotate-3 transition-transform duration-300">
                  <feature.icon className="w-7 h-7 text-white" />
                </div>
                
                {/* Content */}
                <div className="relative">
                  <h4 className="font-bold text-lg mb-2 text-foreground group-hover:text-raven-electric transition-colors">
                    {feature.title}
                  </h4>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Technician Features */}
        <div>
          <h3 className="text-2xl font-bold mb-8 text-center">
            <span className="landing-gradient-text">Para Técnicos</span>
          </h3>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {technicianFeatures.map((feature, index) => (
              <div
                key={index}
                className="group landing-glass-card p-6"
                style={{
                  animation: 'fade-in 0.6s ease-out backwards',
                  animationDelay: `${index * 0.1}s`
                }}
              >
                {/* Gradient overlay on hover */}
                <div className="absolute inset-0 bg-gradient-to-br from-raven-electric/0 to-raven-violet/0 group-hover:from-raven-electric/10 group-hover:to-raven-violet/10 transition-all duration-500 rounded-2xl" />
                
                {/* Icon */}
                <div className="relative mb-4 w-14 h-14 rounded-xl bg-gradient-to-br from-raven-electric to-raven-violet flex items-center justify-center group-hover:scale-110 group-hover:rotate-3 transition-transform duration-300">
                  <feature.icon className="w-7 h-7 text-white" />
                </div>
                
                {/* Content */}
                <div className="relative">
                  <h4 className="font-bold text-lg mb-2 text-foreground group-hover:text-raven-electric transition-colors">
                    {feature.title}
                  </h4>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
